<template>
  <div
    v-if="paginatedHub"
    class="flex md:items-center gap-4 flex-col md:flex-row"
  >
    <h1 class="font-bold text-4xl text-on-background md:whitespace-nowrap">
      <template
        v-if="
          organization?.isDashboardTitleEnabled &&
          Boolean(organization.dashboardTitleText?.length)
        "
      >
        {{ organization.dashboardTitleText }}
      </template>
      <template v-else>
        {{ organization?.name }} {{ $t('common.dashboard') }}
      </template>
    </h1>
    <div class="hidden md:block w-full h-0.5 bg-line"></div>
    <div
      class="hidden md:block font-semibold text-on-background-variant whitespace-nowrap"
    >
      {{ paginatedHub.totalCount }} {{ $t('common.hubs') }}
    </div>
  </div>

  <div v-if="paginatedHub">
    <Breadcrumbs v-if="breadcrumbsItems !== null" :items="breadcrumbsItems" />

    <img
      v-if="organization?.dashboardHeaderImageUrl"
      :src="organization.dashboardHeaderImageUrl"
      alt="Header"
      class="w-full aspect-[3.4] rounded-xl mt-10 object-cover"
    />

    <EmptyList v-if="paginatedHub.nodes.length === 0" class="mt-30" />
    <div
      v-else-if="paginatedHub"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-10"
    >
      <div
        v-for="(hub, hubIndex) in paginatedHub.nodes"
        :key="hubIndex"
        class="bg-surface rounded-xl aspect-[3/4] relative"
        :class="{ 'border-2 border-primary': hub.isLaunchEnabled }"
      >
        <launch-hub-badge
          v-if="hub.isLaunchEnabled"
          class="absolute left-2 top-2"
        />
        <div class="aspect-square w-full flex items-center justify-center">
          <NuxtLink :to="createHubUrl(hub.slug)" class="w-10/12 rounded-full">
            <img
              :src="hub.logoUrl"
              alt="Hub logo"
              class="aspect-square w-full rounded-full object-cover"
            />
          </NuxtLink>
        </div>

        <div class="px-5">
          <hr />

          <NuxtLink
            :to="createHubUrl(hub.slug)"
            class="truncate mt-4 block font-semibold text-on-surface"
          >
            {{ hub.title }}
          </NuxtLink>
          <div class="text-sm font-semibold text-on-surface-variant">
            {{ hub.contentsCount }} {{ $t('common.content-items') }}
          </div>
        </div>
      </div>

      <div
        v-if="
          organization &&
          organization.displayDashboardHelpBox &&
          organization.dashboardHelpBoxCtaButtonUrl
        "
        class="p-9 flex flex-col justify-center bg-secondary text-on-secondary rounded-xl"
      >
        <div>
          <div>{{ organization.dashboardHelpBoxTitle }}</div>
          <div class="font-bold text-3xl mt-3">
            {{ organization.dashboardHelpBoxDescription }}
          </div>
          <Btn
            :to="organization.dashboardHelpBoxCtaButtonUrl"
            :variant="BtnVariant.OUTLINED"
            class="mt-8"
          >
            {{ organization.dashboardHelpBoxCtaButtonText }}
          </Btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useOrganizationDomain } from '#imports';
import { useAsyncData } from 'nuxt/app';
import { apiGetHubsList } from '~/api/hubs';
import { useAuthUserStore } from '~/store/auth-user';
import { EmptyList } from '~/pages/_components/empty-list';
import { Btn, BtnVariant } from '~/components/btn';
import { Breadcrumbs } from '~/components/breadcrumbs';
import { useI18n } from 'vue-i18n';
import { handleApiError } from '~/api';
import { LaunchHubBadge } from '~/pages/_components/hub-dashboard/_components/launch-hub-badge';
import { useOrganizationStore } from '~/store/organization';

const { t } = useI18n();
const authUser = useAuthUserStore();
const { organization } = storeToRefs(useOrganizationStore());
const organizationDomain = useOrganizationDomain();

const { data: paginatedHub, error: hubsError } = useAsyncData('hubs', () => {
  return apiGetHubsList({ perPage: 100 });
});

if (hubsError.value) {
  await handleApiError(hubsError.value);
}

const breadcrumbsItems = computed(() => {
  return organizationDomain !== null || !organization.value
    ? null
    : [
        { label: t('common.dashboard'), to: '/' },
        {
          label: organization.value.name,
          to: `/org/${organization.value.slug}`,
        },
      ];
});

const createHubUrl = (hubSlug: string) => {
  if (organizationDomain !== null || !organization.value) {
    return `/${hubSlug}`;
  }
  return `/org/${organization.value.slug}/${hubSlug}`;
};
</script>
